/**
 * Tax Drawer — Apex Design System.
 *
 * A right-side slide-over that opens from the Tax line in an order's Subtotals
 * section. It exposes every way tax can be entered on an order and shows the
 * resulting per-scheme breakdown:
 *
 *   • Automatic — the tax engine auto-selects matching scheme(s) by condition.
 *   • Scheme    — pin one specific scheme regardless of auto-conditions.
 *   • Percent   — a manual rate (tax = taxable base × %). Re-derives on line changes.
 *   • Amount    — a manual flat tax amount. Frozen across line changes.
 *   • Exempt    — explicitly zero tax.
 *
 * Manual/scheme/exempt modes require a reason and are recorded for audit. The
 * scheme/override detail shown here is intentionally UI-only — it never appears
 * on the printed or emailed order, which shows only the scalar tax amount.
 *
 * Built for purchase orders today; the props are entity-agnostic so sales orders
 * and quotes can adopt it next.
 */

import { useEffect, useMemo, useState } from "react";
import { Calculator, RefreshCw, Shield, Pencil, Ban, Info } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import type { TaxScheme, OrderTaxLine } from "@/types/sales-order";
import ActionDrawer from "@/components/ui/ActionDrawer";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import ComboSelect from "@/components/ui/ComboSelect";
import {
  effectiveTaxRate,
  formatRatePct,
  splitChargeRows,
  taxableBase,
  taxSource,
  TAX_CHIP_CLASS,
  type TaxBasis,
} from "@/utils/taxDisplay";

type EntityType = "sales_order" | "purchase_order" | "quote";
type TaxMode = "auto" | "scheme" | "percent" | "amount" | "exempt";

const ENTITY_API_PREFIX: Record<EntityType, string> = {
  sales_order: "sales-orders",
  purchase_order: "purchase-orders",
  quote: "quotes",
};

const ENTITY_SCHEME_FILTER: Record<EntityType, string[]> = {
  purchase_order: ["purchase", "both"],
  sales_order: ["sales", "both"],
  quote: ["sales", "both"],
};

const MODE_TABS: { value: TaxMode; label: string }[] = [
  { value: "auto", label: "Automatic" },
  { value: "scheme", label: "Scheme" },
  { value: "percent", label: "Percent" },
  { value: "amount", label: "Amount" },
  { value: "exempt", label: "Exempt" },
];

interface TaxDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: EntityType;
  entityId: string;
  taxLines: OrderTaxLine[];
  /**
   * What tax is charged on. Not a bare subtotal: a sales order's base is
   * `subtotal - discount + taxable charges`, and previewing against the subtotal
   * would show an amount the server will not produce.
   */
  basis: TaxBasis;
  taxTotal: number;
  /** Whether tax can be edited (Draft/Submitted). Read-only view otherwise. */
  canEdit: boolean;
  /** React Query key to invalidate after a change. */
  queryKey: readonly unknown[];
}

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(v);

/** Extract a human-readable error from an Axios error response. */
function getErrorDetail(err: unknown): string {
  const axiosErr = err as { response?: { data?: { detail?: string } } };
  return axiosErr?.response?.data?.detail || "An unexpected error occurred";
}

/** Derive the initial editor mode from the current tax lines. */
function initialMode(taxLines: OrderTaxLine[]): TaxMode {
  const only = taxLines.length === 1 ? taxLines[0] : null;
  if (only) {
    if (only.override_mode === "manual_percent") return "percent";
    if (only.override_mode === "manual_amount") return "amount";
    if (only.override_mode === "forced_scheme") return "scheme";
    if (only.override_mode === "exempt") return "exempt";
  }
  return "auto";
}

export default function TaxDrawer({
  isOpen,
  onClose,
  entityType,
  entityId,
  taxLines,
  basis,
  taxTotal,
  canEdit,
  queryKey,
}: TaxDrawerProps) {
  const qc = useQueryClient();
  const prefix = ENTITY_API_PREFIX[entityType];

  const [mode, setMode] = useState<TaxMode>("auto");
  const [schemeId, setSchemeId] = useState("");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");

  // Re-seed the editor whenever the drawer is (re)opened to reflect current state.
  useEffect(() => {
    if (!isOpen) return;
    const m = initialMode(taxLines);
    const only = taxLines.length === 1 ? taxLines[0] : null;
    setMode(m);
    setReason("");
    setSchemeId(m === "scheme" ? (only?.tax_scheme_id ?? "") : "");
    if (m === "percent" && only) setValue(String(Number(only.rate) * 100));
    else if (m === "amount" && only) setValue(String(Number(only.applied_tax)));
    else setValue("");
    // Only re-seed on open, not on every taxLines change (which would clobber typing).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // --- Tax schemes for the scheme picker ---
  const allowedAppliesTo = ENTITY_SCHEME_FILTER[entityType];
  const { data: allSchemes } = useQuery<TaxScheme[]>({
    queryKey: ["tax-schemes"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/tax-schemes")).data as TaxScheme[],
    staleTime: 60_000,
    enabled: isOpen && canEdit,
  });

  const schemeOptions = useMemo(() => {
    if (!allSchemes) return [];
    return allSchemes
      .filter((s) => s.is_active && allowedAppliesTo.includes(s.applies_to))
      .map((s) => ({
        value: s.id,
        label: s.name,
        sublabel:
          s.scheme_type === "percentage"
            ? formatRatePct(Number(s.rate))
            : fmt(Number(s.flat_amount)),
      }));
  }, [allSchemes, allowedAppliesTo]);

  const selectedScheme = useMemo(
    () => allSchemes?.find((s) => s.id === schemeId) ?? null,
    [allSchemes, schemeId],
  );

  // The base the server will charge on, so a preview is a prediction rather
  // than a different calculation.
  const base = taxableBase(basis);

  // --- Live preview of the resulting tax amount for the chosen mode ---
  const previewAmount = useMemo<number | null>(() => {
    if (mode === "scheme") {
      if (!selectedScheme) return null;
      return selectedScheme.scheme_type === "percentage"
        ? base * Number(selectedScheme.rate)
        : Number(selectedScheme.flat_amount);
    }
    if (mode === "percent") {
      const pct = parseFloat(value);
      return isNaN(pct) ? null : base * (pct / 100);
    }
    if (mode === "amount") {
      const amt = parseFloat(value);
      return isNaN(amt) ? null : amt;
    }
    if (mode === "exempt") return 0;
    return null;
  }, [mode, selectedScheme, value, base]);

  const summaryRate = effectiveTaxRate(taxLines, basis);
  const source = taxSource(taxLines);
  // Fee rows share this array but are not tax; they get their own group below.
  const { tax: taxRows, fee: feeRows, feeTotal } = splitChargeRows(taxLines);

  const reasonRequired = mode !== "auto";
  const valid = useMemo(() => {
    if (mode === "auto") return true;
    if (!reason.trim()) return false;
    if (mode === "scheme") return !!schemeId;
    if (mode === "percent" || mode === "amount") {
      const n = parseFloat(value);
      return !isNaN(n) && n >= 0;
    }
    return true; // exempt
  }, [mode, reason, schemeId, value]);

  const applyMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { mode };
      if (mode === "scheme") body.tax_scheme_id = schemeId;
      if (mode === "percent" || mode === "amount")
        body.value = parseFloat(value);
      if (mode !== "auto") body.reason = reason;
      return (await apiClient.post(`/api/v1/${prefix}/${entityId}/tax`, body))
        .data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      setReason("");
      toast.success(
        "Tax updated",
        mode === "auto"
          ? "Taxes recalculated automatically."
          : "The change has been logged for audit.",
      );
    },
    onError: (err) => toast.error("Failed to update tax", getErrorDetail(err)),
  });

  return (
    <ActionDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Taxes"
      description="Choose how tax is calculated for this order."
      icon={Calculator}
      size="lg"
    >
      <div className="space-y-6">
        {/* ── Summary ── */}
        <div className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-sunken)] px-4 py-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
              Current tax
            </span>
            {source && (
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${TAX_CHIP_CLASS[source.tone]}`}
              >
                {source.label}
              </span>
            )}
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-bold tabular-nums text-[var(--text-primary)]">
              {fmt(taxTotal)}
            </span>
            {summaryRate != null && summaryRate > 0 && (
              <span className="text-sm text-[var(--text-tertiary)]">
                effective {formatRatePct(summaryRate)}
              </span>
            )}
          </div>
        </div>

        {/* ── Mode editor (Draft/Submitted only) ── */}
        {canEdit && (
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                How is tax determined?
              </label>
              <div className="inline-flex flex-wrap gap-0.5 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-0.5">
                {MODE_TABS.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setMode(t.value)}
                    className={`rounded-[var(--radius-sm)] px-3 py-1.5 text-xs font-medium transition-all ${
                      mode === t.value
                        ? "border border-[var(--border-default)] bg-[var(--surface-base)] text-[var(--text-primary)] shadow-sm"
                        : "text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (valid) applyMutation.mutate();
              }}
              className="space-y-4"
            >
              {/* Mode-specific inputs */}
              {mode === "auto" && (
                <p className="flex items-start gap-2 text-xs text-[var(--text-secondary)]">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--text-tertiary)]" />
                  The tax engine selects matching scheme(s) automatically based
                  on vendor profile, item categories, and jurisdiction.
                  Recalculate to refresh after changes.
                </p>
              )}

              {mode === "scheme" && (
                <div>
                  <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-[var(--text-tertiary)]">
                    Tax scheme
                  </label>
                  <ComboSelect
                    options={schemeOptions}
                    value={schemeId}
                    onChange={setSchemeId}
                    placeholder="Select a scheme to apply..."
                  />
                </div>
              )}

              {mode === "percent" && (
                <Input
                  label="Tax rate (%)"
                  type="number"
                  step="0.01"
                  min="0"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="e.g. 7.25"
                  required
                />
              )}

              {mode === "amount" && (
                <Input
                  label="Tax amount ($)"
                  type="number"
                  step="0.01"
                  min="0"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="0.00"
                  required
                />
              )}

              {mode === "exempt" && (
                <p className="flex items-start gap-2 text-xs text-[var(--text-secondary)]">
                  <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--text-tertiary)]" />
                  Marks this order as tax exempt (zero tax) with a recorded
                  reason.
                </p>
              )}

              {/* Live preview */}
              {previewAmount != null && mode !== "auto" && (
                <div className="flex items-center justify-between rounded-[var(--radius-md)] bg-[var(--surface-sunken)] px-3 py-2 text-sm">
                  <span className="text-[var(--text-secondary)]">
                    Resulting tax
                  </span>
                  <span className="font-semibold tabular-nums text-[var(--text-primary)]">
                    {fmt(previewAmount)}
                    {mode === "amount" && base > 0 && (
                      <span className="ml-1 text-xs font-normal text-[var(--text-tertiary)]">
                        (≈ {formatRatePct(previewAmount / base)})
                      </span>
                    )}
                  </span>
                </div>
              )}

              {/* Reason (required for non-auto) */}
              {reasonRequired && (
                <Input
                  label="Reason (required for audit)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                  placeholder="e.g. Vendor provided exemption certificate"
                />
              )}

              <div className="flex items-center justify-end gap-2 border-t border-[var(--border-subtle)] pt-4">
                <Button
                  type="submit"
                  size="sm"
                  variant={mode === "auto" ? "secondary" : "primary"}
                  isLoading={applyMutation.isPending}
                  disabled={!valid}
                >
                  {mode === "auto" ? (
                    <>
                      <RefreshCw className="h-3.5 w-3.5" /> Recalculate
                    </>
                  ) : (
                    "Apply"
                  )}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* ── Breakdown ── */}
        <div>
          <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
            Breakdown
          </h3>
          {taxRows.length > 0 ? (
            <div className="divide-y divide-[var(--border-subtle)] rounded-[var(--radius-lg)] border border-[var(--border-subtle)]">
              {taxRows.map((tl) => (
                <div key={tl.id} className="px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-sm font-medium text-[var(--text-primary)]">
                        {tl.scheme_name}
                      </span>
                      <span className="shrink-0 text-xs text-[var(--text-tertiary)]">
                        {tl.scheme_type === "percentage"
                          ? formatRatePct(Number(tl.rate))
                          : fmt(Number(tl.flat_amount))}
                      </span>
                      {tl.is_overridden && (
                        <Badge variant="warning">
                          <Pencil className="mr-0.5 h-3 w-3" />
                          Manual
                        </Badge>
                      )}
                      {tl.certificate_verified && (
                        <Badge variant="success">
                          <Shield className="mr-0.5 h-3 w-3" />
                          Cert
                        </Badge>
                      )}
                    </div>
                    <span className="shrink-0 text-sm font-bold tabular-nums text-[var(--text-primary)]">
                      {fmt(Number(tl.applied_tax))}
                    </span>
                  </div>
                  {tl.is_overridden && tl.override_reason && (
                    <p className="mt-0.5 text-[11px] text-amber-600 dark:text-amber-300">
                      Reason: {tl.override_reason}
                    </p>
                  )}
                  {tl.note && (
                    <p className="mt-0.5 text-[11px] text-[var(--text-tertiary)]">
                      {tl.note}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="rounded-[var(--radius-lg)] border border-dashed border-[var(--border-subtle)] px-3 py-6 text-center text-xs text-[var(--text-tertiary)]">
              No tax applied.
              {canEdit
                ? " Use Automatic to evaluate schemes, or set a manual tax above."
                : ""}
            </p>
          )}
        </div>

        {/* ── Charges ──
            Fee rows live in the same array as tax but are not tax, and they are
            not editable from here: a tax directive deliberately leaves them
            standing. Shown because this is where "why is the total that number"
            gets answered, and an unexplained gap between tax and total is worse
            than a row the user cannot act on. */}
        {feeRows.length > 0 && (
          <div>
            <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
              Order charges — not tax
            </h3>
            <div className="divide-y divide-[var(--border-subtle)] rounded-[var(--radius-lg)] border border-[var(--border-subtle)]">
              {feeRows.map((fee) => (
                <div
                  key={fee.id}
                  className="flex items-center justify-between gap-2 px-3 py-2.5"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm text-[var(--text-primary)]">
                      {fee.scheme_name}
                    </span>
                    {fee.is_taxable && <Badge variant="info">taxable</Badge>}
                  </div>
                  <span className="shrink-0 text-sm tabular-nums text-[var(--text-warning)]">
                    {fmt(Number(fee.applied_tax))}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between px-3 py-2 text-xs">
                <span className="text-[var(--text-secondary)]">
                  Charges total
                </span>
                <span className="tabular-nums font-medium text-[var(--text-warning)]">
                  {fmt(feeTotal)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── Privacy note ── */}
        <p className="flex items-start gap-2 text-[11px] text-[var(--text-tertiary)]">
          <Info className="mt-0.5 h-3 w-3 shrink-0" />
          This breakdown and any override are shown here only — the printed and
          emailed order show just the tax total.
        </p>
      </div>
    </ActionDrawer>
  );
}
